/**
 * Turns a stored challenge email body into readable text.
 *
 * What we persist is the HTML we emailed — `<strong>`, `<p>`, a rates `<table>`,
 * and a `<style>` block. Rendering that as a React text node escapes it, so the
 * carrier reads literal `<p>` tags in the one panel headed "exactly as you
 * received them".
 *
 * Text rather than injected HTML on purpose: this is carrier-facing, and there
 * is no sanitizer in the frontend today. The body is our own generated content,
 * so rendering the markup would be defensible — but that is a deliberate
 * decision with an XSS surface attached, not something to slip in here.
 *
 * The table becomes one line per row, which reads acceptably because the rate
 * tables are label/value pairs.
 */
export function emailBodyToText(html: string | undefined): string {
  if (html === undefined || html === "") return "";

  return (
    html
      // Drop anything that isn't content. `<style>` in particular would
      // otherwise print its CSS as prose.
      .replace(/<(style|script)\b[^>]*>[\s\S]*?<\/\1>/gi, "")
      // Block boundaries become line breaks so paragraphs and table rows stay
      // apart; a table cell boundary becomes a space so a row reads as a line.
      // Both the opening and closing tag break, because a block can follow
      // inline content directly (`</strong><p>`) with no closing tag between.
      .replace(/<\/?(p|div|tr|h[1-6])\b[^>]*>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/t[dh]\s*>/gi, " ")
      .replace(/<[^>]+>/g, "")
      // Entities the email templates actually emit.
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      // Collapse the whitespace the tags left behind, without flattening the
      // paragraph breaks we just created.
      .split("\n")
      .map((line) => line.replace(/[ \t]+/g, " ").trim())
      .filter((line) => line !== "")
      .join("\n")
  );
}
